/**
 * The candidacy status vocabulary — one source of truth for the admin
 * dropdown, the Notion Cycle DB sync, and the public API.
 *
 * Status describes where a candidacy sits in the ballot lifecycle, and
 * nothing else. Roughly in order:
 *
 *   prospective  — we think they might run; nothing official yet
 *   announced    — they've publicly declared
 *   candidate    — filed / on the ballot
 *   write_in     — running as a write-in candidate
 *   challenged   — petitions contested; ballot access in dispute
 *   removed      — knocked off the ballot by a successful challenge
 *   withdrawn    — voluntarily dropped out before the ballot was set, so the
 *                  name is not printed (contrast with `removed`)
 *   withdrawn_on_ballot — dropped out after the ballot was set; still
 *                  printed, can still receive votes and even win
 *   advanced     — qualified for a later ballot, such as primary to general
 *                  or general to runoff; the destination status is candidate
 *   lost         — ran, didn't win
 *   won          — ran, won
 *   won_write_in / advanced_write_in / lost_write_in — the same results for
 *                  a write-in, so the record keeps how they ran (ADR 0018)
 *
 * Status is recorded per ballot (ADR 0013, `candidacy_ballots`), so it is
 * scoped to that Election: `advanced` on a primary means nomination;
 * `won` is reserved for winning the office on a general or runoff.
 *
 * Deliberately NOT a status: incumbency. Holding the seat is a property
 * of the person-in-the-race, not a stage of the race — an incumbent can
 * have any of the statuses above. That lives on `candidacies.is_incumbent`,
 * fed by the `Incumbent` checkbox on the Notion Cycle DB row.
 */

export const CANDIDACY_STATUSES = [
  "prospective",
  "announced",
  "candidate",
  "write_in",
  "challenged",
  "removed",
  "withdrawn",
  "withdrawn_on_ballot",
  "advanced",
  "advanced_write_in",
  "lost",
  "lost_write_in",
  "won",
  "won_write_in",
] as const;

export type CandidacyStatus = (typeof CANDIDACY_STATUSES)[number];

/**
 * Used when a Notion row's Status select is blank and when admin creates
 * a candidacy without picking one. `prospective` is the conservative
 * floor: an untriaged row claims nothing about ballot access.
 */
export const DEFAULT_CANDIDACY_STATUS: CandidacyStatus = "prospective";

/**
 * Pre-existing values from the older vocabulary. Rows carrying these were
 * deliberately left in place rather than bulk-rewritten, so every surface
 * that renders or edits a status still has to cope with them.
 */
export const LEGACY_CANDIDACY_STATUSES = [
  "active",
  "withdrew",
  "incumbent",
] as const;

/**
 * Aliases folded into the canonical set on the way in. Only pure renames
 * belong here — `withdrew` and `withdrawn` are the same concept spelled
 * two ways. `active` and `incumbent` are NOT aliased: `active` has no
 * unambiguous new home (announced vs candidate is a real distinction)
 * and `incumbent` is no longer a status at all, so both are preserved
 * verbatim for a human to re-classify.
 */
const STATUS_ALIASES: Record<string, CandidacyStatus> = {
  withdrew: "withdrawn",
  "write-in": "write_in",
  "write in": "write_in",
  "withdrawn (on ballot)": "withdrawn_on_ballot",
  "won (write-in)": "won_write_in",
  "advanced (write-in)": "advanced_write_in",
  "lost (write-in)": "lost_write_in",
};

const LABELS: Record<string, string> = {
  write_in: "Write-In",
  withdrawn_on_ballot: "Withdrawn (on ballot)",
  won_write_in: "Won (write-in)",
  advanced_write_in: "Advanced (write-in)",
  lost_write_in: "Lost (write-in)",
};

const CANONICAL = new Set<string>(CANDIDACY_STATUSES);

/**
 * Fold a human-entered status into the canonical vocabulary.
 *
 * Matching is case- and whitespace-insensitive, so a Notion select can
 * use pretty labels ("Prospective", "On the ballot" → no, that one won't
 * match) while Backbone stores lowercase. An unrecognized value is
 * preserved trimmed rather than discarded or coerced — losing what the
 * committee typed would be worse than storing something off-vocabulary,
 * and it shows up as an unstyled badge in admin, which is the signal.
 */
export function normalizeCandidacyStatus(
  raw: string | null | undefined
): string {
  const trimmed = raw?.trim();
  if (!trimmed) return DEFAULT_CANDIDACY_STATUS;
  const lowered = trimmed.toLowerCase();
  if (CANONICAL.has(lowered)) return lowered;
  const alias = STATUS_ALIASES[lowered];
  if (alias) return alias;
  return trimmed;
}

/** Human-facing status label for admin controls. */
export function candidacyStatusDisplayLabel(status: string): string {
  return LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
}

/** True when `status` is a canonical value. */
export function isCanonicalCandidacyStatus(
  status: string
): status is CandidacyStatus {
  return CANONICAL.has(status);
}

/**
 * The options to show in a status dropdown for a candidacy currently set
 * to `current`. Canonical values always; plus `current` itself when it's
 * off-vocabulary, so opening and saving an old row never silently
 * rewrites its status to whatever happened to be first in the list.
 */
export function statusOptionsFor(current: string | null | undefined,
  ballot?: { stage: string; hasNextBallot: boolean }
): string[] {
  const options: string[] = CANDIDACY_STATUSES.filter(status => !ballot ||
    (resultKind(status) !== "won" || ballot.stage !== "primary") &&
    (resultKind(status) !== "advanced" || ballot.stage === "primary" || ballot.hasNextBallot));
  if (current && !CANONICAL.has(current)) options.unshift(current);
  return options;
}

/**
 * Which result a status records on its ballot, or null when it is not a
 * result. Every rule that asks "did they win / advance / lose?" goes
 * through here (ADR 0018), never through a literal comparison.
 */
export function resultKind(status: string | null | undefined): "won" | "advanced" | "lost" | null {
  switch (status) {
    case "won": case "won_write_in": return "won";
    case "advanced": case "advanced_write_in": return "advanced";
    case "lost": case "lost_write_in": return "lost";
    default: return null;
  }
}

/** A write-in campaign, before or after its result. */
export function isWriteIn(status: string | null | undefined): boolean {
  return status === "write_in" || status === "won_write_in" ||
    status === "advanced_write_in" || status === "lost_write_in";
}

/** The status a Won / Advanced / Lost tap records, keeping a write-in a write-in. */
export function resultStatusFor(
  tap: "won" | "advanced" | "lost",
  current: string | null | undefined
): CandidacyStatus {
  // Lost changes nothing for an on-ballot withdrawal: it already counts as
  // not winning, and only a deliberate Won replaces it (ADR 0018).
  if (tap === "lost" && current === "withdrawn_on_ballot") return "withdrawn_on_ballot";
  return isWriteIn(current) ? `${tap}_write_in` : tap;
}

const OFF_BALLOT = new Set(["withdrawn", "withdrew", "removed"]);

/** Statuses the public ballot tool leaves out, for SQL filters (see onPublicBallot). */
export const OFF_PUBLIC_BALLOT = [...OFF_BALLOT, "write_in", "lost_write_in"] as const;

/** On the Results page: everyone a voter can choose here, printed or written in. */
export function onResultsPage(status: string | null | undefined): boolean {
  return !OFF_BALLOT.has(status ?? "");
}

/**
 * On the public ballot tool. Write-ins are left off until they win or
 * advance; the Person page still shows every status (ADR 0018).
 */
export function onPublicBallot(status: string | null | undefined): boolean {
  return !(OFF_PUBLIC_BALLOT as readonly string[]).includes(status ?? "");
}

/**
 * True when election night has nothing left to record for this status.
 * Withdrawn (on ballot) counts as a result, the same as Lost; only a
 * deliberate Won changes it. Off-ballot rows are filtered by onResultsPage.
 */
export function hasResult(status: string | null | undefined): boolean {
  return resultKind(status) !== null || status === "withdrawn_on_ballot";
}

/** The status a result returns to when it is undone. */
export function pendingStatusFor(status: string | null | undefined): string {
  return isWriteIn(status) ? "write_in" : "candidate";
}
